import React, { useEffect, useState } from "react";
import DataTableBase from "../../Components/Common/DataTableBase/DataTableBase";
import { useTranslation } from "react-i18next";
import { useDeviceStore } from "../../stores/Assets/device";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledTooltip,
} from "reactstrap";
import AddDevice from "./Components/AddDevice";
import EditDevice from "./Components/EditDevice";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useConfirmDialogStore } from "../../stores/Modal/ConfirmDialogStore";
import { Pagination } from "../../Components/Common/DataTableBase/Pagination";
import { useSpaceStore } from "../../stores/Assets/space";
import { canEdit } from "../../helpers/permissions";
import SearchBar from "../../Components/Common/SearchBar";
import Skeleton from "../../Components/Common/Skeleton";

const Devices = () => {
  const { t } = useTranslation();
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  document.title = t("Clickorder Admin - Clickers");
  const location = useLocation();
  const deviceId = new URLSearchParams(location.search).get("deviceId");
  const { getDevices, isLoading, devices, deleteDevice, pagination } =
    useDeviceStore((state) => state);
  const { getUserSpaces } = useSpaceStore((state) => state);
  const [spaces, setSpaces] = useState([]);
  const [selectedSpaceId, setSelectedSpaceId] = useState("All");
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [showSpaceError, setShowSpaceError] = useState(false);

  useEffect(() => {
    const fetchSpaces = async () => {
      const spaceList = await getUserSpaces();
      setSpaces(spaceList);
    };
    fetchSpaces();
  }, [getUserSpaces]);

  useEffect(() => {
    getDevices(null, null, rowsPerPage);
  }, []);

  useEffect(() => {
    setTotalRows(pagination?.totalItems || 0);
  }, [pagination]);

  const columns = [
    {
      name: t("Device ID"),
      width: "120px",
      selector: (row) => row?.device_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.device_id}</span>
        </div>
      ),
    },
    {
      name: t("Space"),
      selector: (row) => row?.space?.space_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.space?.name &&
              `${row?.space?.name} (id: ${row?.space?.space_id})`}
          </span>
        </div>
      ),
    },
    {
      name: t("Status"),
      width: "100px",
      selector: (row) => row?.is_active,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.is_active ? (
              <i
                className="ri-checkbox-circle-fill text-success"
                style={{ fontSize: "28px" }}
              ></i>
            ) : (
              <i
                className="ri-close-circle-fill text-danger"
                style={{ fontSize: "28px" }}
              ></i>
            )}
          </span>
        </div>
      ),
    },
    {
      name: t("Button 1"),
      width: "120px",
      selector: (row) => (row?.button_1 ? t("Configured") : t("Not Configured")),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span
            style={{ fontSize: "12px" }}
            className={`badge ${row?.button_1 ? "bg-success" : "bg-warning"
              } text-uppercase`}
          >
            {row?.button_1 ? t("Configured") : t("Not Set")}
          </span>
        </div>
      ),
    },
    {
      name: t("Button 2"),
      width: "120px",
      selector: (row) => (row?.button_2 ? t("Configured") : t("Not Configured")),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span
            style={{ fontSize: "12px" }}
            className={`badge ${row?.button_2 ? "bg-success" : "bg-warning"
              } text-uppercase`}
          >
            {row?.button_2 ? t("Configured") : t("Not Set")}
          </span>
        </div>
      ),
    },
    {
      name: t("Button 3"),
      width: "120px",
      selector: (row) => (row?.button_3 ? t("Configured") : t("Not Configured")),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span
            style={{ fontSize: "12px" }}
            className={`badge ${row?.button_3 ? "bg-success" : "bg-warning"
              } text-uppercase`}
          >
            {row?.button_3 ? t("Configured") : t("Not Set")}
          </span>
        </div>
      ),
    },
    {
      name: t("Button 4"),
      width: "120px",
      selector: (row) => (row?.button_4 ? t("Configured") : t("Not Configured")),
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span
            style={{ fontSize: "12px" }}
            className={`badge ${row?.button_4 ? "bg-success" : "bg-warning"
              } text-uppercase`}
          >
            {row?.button_4 ? t("Configured") : t("Not Set")}
          </span>
        </div>
      ),
    },
    {
      name: t("Created At"),
      selector: (row) => row?.createdAt,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.createdAt
              ? new Date(row.createdAt).toLocaleDateString()
              : "-"}
          </span>
        </div>
      ),
    },
  ];

  const searchHandler = (searchText) => {
    setSearchTerm(searchText);
  };
  const { showConfirm } = useConfirmDialogStore((state) => state);

  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);
  const toggleAddDeviceModal = () => {
    if (spaces.length === 0) {
      setShowSpaceError(true);
      return;
    }
    setShowAddDeviceModal(!showAddDeviceModal);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const toggleEditDeviceModal = (row) => {
    setSelectedRow(row);
    setShowEditDeviceModal(!showEditDeviceModal);
  };

  const onChangePage = (page) => {
    const spaceId = selectedSpaceId === "All" ? null : parseInt(selectedSpaceId);
    getDevices(spaceId, page, rowsPerPage, searchTerm);
  };

  // Auto-apply filters when they change
  useEffect(() => {
    const spaceId = selectedSpaceId === "All" ? null : parseInt(selectedSpaceId);
    getDevices(spaceId, 1, rowsPerPage, searchTerm);
  }, [selectedSpaceId, searchTerm]);

  const deleteDeviceFun = async (id) => {
    try {
      await deleteDevice(id);
    } catch (e) {
      console.log(e);
    }
  };

  const handleSpaceSelect = (e) => {
    const id = e.target.value;
    setSelectedSpaceId(id);
  };

  const [currentDevices, setCurrentDevices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const onPageChange = (page) => {
    setCurrentPage(page);
    getDevices(
      selectedSpaceId === "All" ? null : parseInt(selectedSpaceId),
      page,
      rowsPerPage
    );
  };

  const [canEditDevice, setCanEditDevice] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      const res = await canEdit("device");
      setCanEditDevice(res);
    };

    checkPermission();
  }, []);

  const navigate = useNavigate();

  return (
    <>
      {showSpaceError && (
        <Modal isOpen={showSpaceError} toggle={() => setShowSpaceError(false)}>
          <ModalHeader
            className={"flex align-items-center justify-content-center"}
          >
            <h4 className="m-0">{t("No Registered Space")}</h4>
          </ModalHeader>
          <ModalBody>
            <div className="text-center ">
              <p className="text-danger">
                {t("You should first add a space.")}
              </p>
              <Link to="/spaces" className="btn btn-primary mr-5">
                {t("Add Space")}
              </Link>
            </div>
          </ModalBody>
        </Modal>
      )}
      <DataTableBase
        tableTitle={t("Clickers")}
        customHeader={
          spaces.length === 0 ? (
            // Skeleton loading state
            <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
              <div className="w-100" style={{ flex: '0 0 10%' }}>
                <Skeleton variant="button" />
              </div>
              <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2" style={{ flex: '0 0 60%' }}>
                <div className="d-flex align-items-center gap-2 w-100">
                  <h6 className="text-nowrap mb-0">{t("Search:")} </h6>
                  <div className="flex-grow-1">
                    <Skeleton variant="input" />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 w-100">
                  <h6 className="text-nowrap mb-0">{t("Space:")} </h6>
                  <div className="flex-grow-1">
                    <Skeleton variant="input" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
              {canEditDevice ? (
                <Button
                  color="success"
                  onClick={toggleAddDeviceModal}
                  className="d-flex align-items-center justify-content-center w-100"
                  style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize: "13px" }}
                >
                  + {t("Add Clicker")}
                </Button>
              ) : null}
              <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2" style={{ flex: '0 0 60%' }}>
                <div className="d-flex align-items-center gap-2 w-100">
                  <h6 className="text-nowrap mb-0">{t("Search:")} </h6>
                  <div className="flex-grow-1">
                    <SearchBar
                      onSearch={searchHandler}
                      placeholder={t("Search clickers by ID...")}
                    />
                  </div>
                </div>
                {spaces.length > 0 && (
                  <div className="d-flex align-items-center gap-2 w-100">
                    <h6 className="text-nowrap mb-0">{t("Select Space:")} </h6>
                    <select
                      className="form-control flex-grow-1"
                      value={selectedSpaceId}
                      onChange={handleSpaceSelect}
                    >
                      <option key={"all"} value={"All"}>
                        {t("All")}
                      </option>
                      {spaces.map((space) => (
                        <option key={space.space_id} value={space.space_id}>
                          {space.space_id} - {space.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )
        }
        data={devices}
        columns={columns}
        loading={isLoading}
        paginationTotalRows={totalRows}
        onChangePage={onChangePage}
        onHeaderAddBtnClick={null}
        onSearchIconClick={searchHandler}
        actionColWidth="100px"
        showSearch={false}
        showActionColumn={canEditDevice}
        showSubHeader={false}
        showActionButtons={false}
        customActionBtns={(row) => (
          <>
            <button
              className="btn btn-sm btn-warning"
              onClick={() => {
                if (canEditDevice) {
                  toggleEditDeviceModal(row);
                }
              }}
              title={t("Edit")}
            >
              <i className="ri-edit-fill"></i>{" "}
            </button>

            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                if (canEditDevice) {
                  showConfirm(
                    () => {
                      deleteDeviceFun(row.device_id);
                    },
                    () => {
                      console.log(t("Cancelled"));
                    },
                    t("Confirmation"),
                    `${t("Are you sure you want to delete Clicker")} ${row?.device_id}?`
                  );
                }
              }}
              title={t("Delete")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      />

      {showAddDeviceModal && !showSpaceError && (
        <AddDevice
          spaceList={spaces}
          toggleAddDeviceModal={toggleAddDeviceModal}
          showAddDeviceModal={showAddDeviceModal}
        />
      )}

      {selectedRow && (
        <EditDevice
          spaceList={spaces}
          toggleEditDeviceModal={toggleEditDeviceModal}
          showEditDeviceModal={showEditDeviceModal}
          rowData={selectedRow}
        />
      )}
    </>
  );
};

export default Devices;
